"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const trustLogos = [{
  id: "hplc-validation",
  name: "HPLC Purity Validation",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/hplc_validation_logo.png"
}, {
  id: "mass-spec",
  name: "Mass Spectrometry Analysis",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/mass_spec_logo.png"
}, {
  id: "iso-certified",
  name: "ISO 9001 Certified Facility",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/iso_9001_logo.png"
}, {
  id: "gmp-compliant",
  name: "GMP Manufacturing Standards",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/gmp_standard_logo.png"
}, {
  id: "third-party",
  name: "Independent Third Party Tested",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/third_party_testing_logo.png"
}];
export default function InvestorTrustCertification() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section ref={ref} className={`py-4 lg:py-6 bg-white transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto space-y-2">
          <div className="text-center space-y-1">
            <h2 className="text-sm lg:text-base font-normal text-foreground uppercase tracking-widest">
              Investor Trust & Certification
            </h2>
            <p className="text-xs text-muted-foreground max-w-2xl mx-auto">
              Our commitment to purity is backed by rigorous third-party validation and international manufacturing standards.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {trustLogos.map((logo, i) => <div key={logo.id} data-index={i} className="flex flex-col items-center text-center p-3 border border-border rounded-sm bg-card hover:bg-accent transition-colors">
                <div className="h-12 w-12 mb-2 flex items-center justify-center">
                  <img src={logo.src} alt={logo.name} className="max-h-full max-w-full object-contain grayscale opacity-80" data-wvc-srcset={`${logo.src}-wvc-srcset`} data-wvc-sizes={`${logo.src}-wvc-sizes`} loading="lazy" />
                </div>
                <h3 className="text-xs font-medium text-foreground leading-tight">
                  {logo.name}
                </h3>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Verified Standard
                </p>
              </div>)}
          </div>

          <div className="pt-2 text-center">
            <button className="px-4 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors font-normal text-xs rounded-sm">
              View All Certificates
            </button>
          </div>
        </div>
      </div>
    </section>;
}